import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { personalizedDashboardService } from '../../services/personalizedDashboardService';

// Async thunk to fetch user favorites
export const fetchUserFavorites = createAsyncThunk(
  'favorites/fetchUserFavorites',
  async (userId) => {
    const favorites = await personalizedDashboardService.getUserFavorites(userId);
    return favorites;
  }
);

// Async thunk to save user favorites
export const saveUserFavorites = createAsyncThunk(
  'favorites/saveUserFavorites',
  async ({ userId, favorites }) => {
    const response = await personalizedDashboardService.saveUserFavorites(userId, favorites);
    return response.favorites;
  }
);

const initialState = {
  favorites: [],
  loading: false,
  error: null,
  lastUpdated: null
};

const favoritesSlice = createSlice({
  name: 'favorites',
  initialState,
  reducers: {
    setFavorites: (state, action) => {
      state.favorites = action.payload;
      state.lastUpdated = Date.now();
    },
    addFavorite: (state, action) => {
      if (!state.favorites.find(fav => fav.id === action.payload.id)) {
        state.favorites.push(action.payload);
        state.lastUpdated = Date.now();
      }
    },
    removeFavorite: (state, action) => {
      state.favorites = state.favorites.filter(fav => fav.id !== action.payload);
      state.lastUpdated = Date.now();
    },
    clearFavorites: (state) => {
      state.favorites = [];
      state.lastUpdated = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Handle fetchUserFavorites
    builder
      .addCase(fetchUserFavorites.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserFavorites.fulfilled, (state, action) => {
        state.loading = false;
        state.favorites = action.payload;
        state.lastUpdated = Date.now();
        state.error = null;
      })
      .addCase(fetchUserFavorites.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });

    // Handle saveUserFavorites
    builder
      .addCase(saveUserFavorites.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveUserFavorites.fulfilled, (state, action) => {
        state.loading = false;
        state.favorites = action.payload;
        state.lastUpdated = Date.now();
        state.error = null;
      })
      .addCase(saveUserFavorites.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  }
});

export const {
  setFavorites,
  addFavorite,
  removeFavorite,
  clearFavorites,
  clearError
} = favoritesSlice.actions;

// Selectors
export const selectFavorites = (state) => state.favorites.favorites;
export const selectFavoritesLoading = (state) => state.favorites.loading;
export const selectFavoritesError = (state) => state.favorites.error;
export const selectFavoritesLastUpdated = (state) => state.favorites.lastUpdated;

export default favoritesSlice.reducer;
